import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';

import { PrestadorAlterarSituacaoRequest, PrestadorReativacaoGruposRequest, TITULO_INADIMPLENCIA_CONFIRMAR } from '@/core/api/sicom-api/registro/prestador/prestador-situacao.model';
import { PrestadorSituacaoService } from '@/core/api/sicom-api/registro/prestador/prestador-situacao.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { DateUtils } from '@/shared/utils/date-utils';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
export interface OpcaoSituacao {
    label: string;
    value: string;
}

// Situações a partir das quais a volta para "1 - Ativo" é uma reativação (mostra os grupos).
const SITUACOES_REATIVAVEIS = [5, 9, 10];
// Situações de cancelamento que disparam o alerta de inadimplência.
const SITUACOES_INADIMPLENCIA = [6, 7];
const SITUACAO_ATIVO = 1;
const CONFIRM_KEY = 'alterarSituacaoInadimplencia';

@Component({
    selector: 'app-alterar-situacao',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, Dialog, Select, DatePickerModule, Checkbox, Button, ConfirmDialog],
    providers: [ConfirmationService],
    templateUrl: './alterar-situacao.html'
})
export class AlterarSituacao {
    readonly visible = model<boolean>(false);
    /**
     * Chave de idempotência da sessão do diálogo. Precisa sobreviver ao reenvio com
     * `confirmaInadimplencia: true`: é a mesma intenção do usuário, e o backend liberou a chave ao
     * responder o 409 de confirmação, então a segunda tentativa passa normalmente.
     */
    private chaveIdempotencia = novaChaveIdempotencia();
    readonly pessoa = input<Pessoa | null>(null);
    readonly situacaoAtual = input<number | null>(null);
    readonly opcoes = input<OpcaoSituacao[]>([]);
    // Situação/data já escolhidas no combobox de Dados Gerais; pré-preenchem o formulário ao abrir.
    readonly novaSituacaoInicial = input<number | null>(null);
    readonly dtSituacaoInicial = input<string | null>(null);
    readonly concluido = output<void>();

    private readonly destroyRef = inject(DestroyRef);
    private readonly fb = inject(FormBuilder);
    private readonly prestadorSituacaoService = inject(PrestadorSituacaoService);
    private readonly notificacoesService = inject(NotificacoesService);
    private readonly confirmationService = inject(ConfirmationService);

    readonly salvando = signal(false);
    readonly maxDate = new Date();
    readonly confirmKey = CONFIRM_KEY;

    readonly form: FormGroup = this.fb.group({
        novaSituacao: [null as string | null, Validators.required],
        dtSituacao: [null as Date | null, [Validators.required, this.dataNaoFuturaValidator()]],
        comissaoEtica: [false],
        corpoClinico: [false],
        diretoria: [false],
        corpoSocietario: [false]
    });

    private readonly novaSituacaoSignal = signal<number | null>(null);

    readonly mostrarGrupos = computed(() => {
        const atual = this.situacaoAtual();
        const nova = this.novaSituacaoSignal();
        return nova === SITUACAO_ATIVO && atual != null && SITUACOES_REATIVAVEIS.includes(atual);
    });

    constructor() {
        this.form
            .get('novaSituacao')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((valor) => this.novaSituacaoSignal.set(valor != null && valor !== '' ? Number(valor) : null));

        // Reseta o formulário sempre que o diálogo é aberto.
        effect(() => {
            if (this.visible()) {
                untracked(() => {
                    this.chaveIdempotencia = novaChaveIdempotencia();
                    this.resetarFormulario();
                });
            }
        });
    }

    fechar(): void {
        this.visible.set(false);
    }

    salvar(): void {
        if (this.form.invalid || this.salvando()) {
            this.form.markAllAsTouched();
            return;
        }

        const request = this.construirRequest();
        if (!request) {
            return;
        }

        if (SITUACOES_INADIMPLENCIA.includes(request.novaSituacao)) {
            this.verificarInadimplenciaEEnviar(request);
            return;
        }

        this.enviar(request);
    }

    private verificarInadimplenciaEEnviar(request: PrestadorAlterarSituacaoRequest): void {
        const pessoa = this.pessoa();
        if (!pessoa) {
            return;
        }

        this.salvando.set(true);
        this.prestadorSituacaoService
            .verificarInadimplente(pessoa)
            .pipe(
                finalize(() => this.salvando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (resposta) => {
                    if (resposta.inadimplente) {
                        this.confirmarInadimplencia(resposta.mensagem, request);
                    } else {
                        this.enviar(request);
                    }
                },
                error: () => {}
            });
    }

    private confirmarInadimplencia(mensagem: string | null | undefined, request: PrestadorAlterarSituacaoRequest): void {
        this.confirmationService.confirm({
            key: CONFIRM_KEY,
            header: 'Confirmação de inadimplência',
            message: mensagem?.trim() || 'Existe anuidade em aberto. Deseja continuar?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => this.enviar({ ...request, confirmaInadimplencia: true })
        });
    }

    private enviar(request: PrestadorAlterarSituacaoRequest): void {
        this.salvando.set(true);
        this.prestadorSituacaoService
            .alterarSituacao(request, comIdempotencia(this.chaveIdempotencia))
            .pipe(
                finalize(() => this.salvando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Situação alterada',
                        detail: 'Situação do prestador alterada com sucesso.'
                    });
                    this.visible.set(false);
                    this.concluido.emit();
                },
                error: (erro: HttpErrorResponse) => {
                    // Defensivo: caso o servidor sinalize inadimplência não confirmada (409),
                    // abre o diálogo de confirmação e reenvia com confirmaInadimplencia=true.
                    if (this.ehErroInadimplencia(erro) && !request.confirmaInadimplencia) {
                        this.confirmarInadimplencia(this.extrairMensagem(erro), request);
                        return;
                    }
                    // Todo 409 nesta rota é suprimido globalmente (ver IGNORE_ERRORS em app.config.ts).
                    // Qualquer 409 que não seja o de inadimplência (hoje inexistente, mas possível no
                    // futuro) precisa ser exibido aqui, senão fica invisível para o usuário.
                    if (erro?.status === 409) {
                        this.notificacoesService.notificar({
                            severity: 'error',
                            summary: 'Não foi possível alterar a situação',
                            detail: this.extrairMensagem(erro) || 'Ocorreu um erro ao alterar a situação do prestador.'
                        });
                    }
                    // Demais erros (ex.: 400 bloqueio 5→10) são exibidos pelo interceptor global.
                }
            });
    }

    private ehErroInadimplencia(erro: HttpErrorResponse): boolean {
        return erro?.status === 409 && erro?.error?.title === TITULO_INADIMPLENCIA_CONFIRMAR;
    }

    private extrairMensagem(erro: HttpErrorResponse): string | null {
        const detalhe = erro?.error?.detail;
        if (Array.isArray(detalhe)) {
            return detalhe.filter(Boolean).join('\n');
        }
        if (typeof detalhe === 'string' && detalhe.trim()) {
            const texto = detalhe.trim();
            if (texto.startsWith('[') && texto.endsWith(']')) {
                return texto
                    .slice(1, -1)
                    .replace(/^["']+|["']+$/g, '')
                    .trim();
            }
            return texto;
        }
        return null;
    }

    private construirRequest(): PrestadorAlterarSituacaoRequest | null {
        const pessoa = this.pessoa();
        const raw = this.form.getRawValue();
        const novaSituacao = raw.novaSituacao != null && raw.novaSituacao !== '' ? Number(raw.novaSituacao) : null;
        const dtSituacao = DateUtils.formatDateToApi(raw.dtSituacao);

        if (!pessoa || novaSituacao == null || !dtSituacao) {
            return null;
        }

        const grupos: PrestadorReativacaoGruposRequest | null = this.mostrarGrupos()
            ? {
                  comissaoEtica: !!raw.comissaoEtica,
                  corpoClinico: !!raw.corpoClinico,
                  diretoria: !!raw.diretoria,
                  corpoSocietario: !!raw.corpoSocietario
              }
            : null;

        return {
            id: pessoa.id,
            uf: pessoa.uf,
            novaSituacao,
            dtSituacao,
            grupos,
            confirmaInadimplencia: false
        };
    }

    private resetarFormulario(): void {
        const nova = this.novaSituacaoInicial();
        const dataInicial = this.dtSituacaoInicial();
        this.form.reset({
            novaSituacao: nova != null ? String(nova) : null,
            dtSituacao: dataInicial ? DateUtils.parseToDate(dataInicial) : null,
            comissaoEtica: false,
            corpoClinico: false,
            diretoria: false,
            corpoSocietario: false
        });
        this.novaSituacaoSignal.set(nova ?? null);
        this.salvando.set(false);
    }

    private dataNaoFuturaValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = control.value;
            if (!valor || !(valor instanceof Date)) {
                return null;
            }
            const hoje = new Date();
            hoje.setHours(23, 59, 59, 999);
            return valor.getTime() > hoje.getTime() ? { dataFutura: true } : null;
        };
    }
}
